import React, { useEffect, useMemo, useRef } from 'react';
import type { Content } from '@jwp/ott-common/types/config';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';

import type { ScreenComponent } from '../../../../../types/screens';
import ShelfList from '../../../../containers/ShelfList/ShelfList';
import Hero from '../../../../components/Hero/Hero';

import IconButton from '@jwp/ott-ui-react/src/components/IconButton/IconButton';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import { handleBackNavigate } from '@jwp/ott-ui-react/src/utils/handleBackNavigate';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import styles from './MediaHub.module.scss';

const parsePlaylistIds = (input: unknown): Content[] => {
  const playlistIds = typeof input === 'string' ? input.replace(/\s+/g, '').split(',') : [];

  return playlistIds.map((id) => ({ type: 'playlist', contentId: id }));
};

const MediaHub: ScreenComponent<PlaylistItem> = ({ data }) => {
  // Hub may specify multiple playlists as a CSV list of IDs or a single playlist
  const rows = useMemo(() => parsePlaylistIds(data.playlists || data.playlist), [data.playlist, data.playlists]);

  //custom history stack
  const addToHistoryStack = useNavigationStack((s) => s.push);

  // Effects
  useEffect(() => {
    (document.scrollingElement || document.body).scroll({ top: 0 });
    (document.querySelector('#video-details button') as HTMLElement)?.focus();
  }, [data]);

  useEffect(() => {
    addToHistoryStack(`${window.location.pathname}${window.location.search}`);
  }, []);

  const pageId = window.location.pathname;

  const pageState = useContentStateStore((s) => s.pages[pageId]);
  const setPageState = useContentStateStore((s) => s.setPageState);

  const restoredRef = useRef(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!restoredRef.current && pageState?.scrollPosition) {
      window.scrollTo(0, pageState.scrollPosition);
      restoredRef.current = true;
    }

    const handleScroll = () => {
      setPageState(pageId, { scrollPosition: window.scrollY, from: 'hub' });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageId, pageState?.scrollPosition, setPageState]);
  // useContentStateStore.setState({ click: { label: data.title, contentId: data.mediaid, type: 'content_list' } });
  useContentStateStore.setState({ activeScreen: data.title });

  return (
    <header>
      <IconButton aria-label={t('common:back')} onClick={() => handleBackNavigate(navigate)} className={styles.backButton}>
        <Icon icon={ArrowLeft} />
      </IconButton>
      <Hero image={data.backgroundImage} title={data.title} description={data.description} data={data} isHubContent={true} />
      <ShelfList rows={rows} isHubContent={true} pageId={pageId}></ShelfList>
    </header>
  );
};

export default MediaHub;
